import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import loginbackground from "../shared/assets/background-images/backgroundimg.png";
import menubackground from "../shared/assets/background-images/overlayrimg.png";
import Rectangle from "../shared/assets/background-images/Rectangle.png";
import Checkbox, { CheckboxProps } from "@mui/material/Checkbox";
import { styled } from "@mui/material/styles";
import { FaEye, FaEyeSlash } from "react-icons/fa";

const CustomCheckbox = styled((props: CheckboxProps) => (
  <Checkbox
    disableRipple
    color="default"
    icon={<span className="checkbox-icon" />}
    checkedIcon={<span className="checkbox-icon checked" />}
    {...props}
  />
))(() => ({
  "&:hover": { backgroundColor: "transparent" },
  "& .checkbox-icon": {
    width: 20,
    height: 20,
    borderRadius: 6,
    border: "2px solid #fb9c18",
    display: "inline-block",
    transition: "all 0.3s ease",
    backgroundColor: "transparent",
    position: "relative",
  },
  "& .checkbox-icon.checked": {
    backgroundColor: "#fb9c18",
    borderColor: "#fb9c18",
  },
  "& .checkbox-icon.checked::after": {
    content: '""',
    position: "absolute",
    left: "5px",
    top: "2px",
    width: "6px",
    height: "10px",
    border: "solid black",
    borderWidth: "0 2px 2px 0",
    transform: "rotate(45deg)",
  },
}));

const containerVariants = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { staggerChildren: 0.15 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  show: { opacity: 1, y: 0, transition: { duration: 0.4, ease: "easeOut" } },
};

function SignupPage() {
  const [step, setStep] = useState(1);
  const [checked, setChecked] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showRepeatPassword, setShowRepeatPassword] = useState(false);
  const [formData, setFormData] = useState({
    first: "",
    last: "",
    username: "",
    email: "",
    password: "",
    repeatPassword: "",
  });
  const [errors, setErrors] = useState<any>({});

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const validateStep = () => {
    const newErrors: any = {};
    if (step === 1 && (!formData.first || !formData.last))
      newErrors.name = "Please enter both first and last name.";
    if (step === 2 && !formData.username)
      newErrors.username = "Username is required.";
    if (step === 3) {
      if (!formData.email) newErrors.email = "Email is required.";
      else if (!emailRegex.test(formData.email))
        newErrors.email = "Please enter a valid email address.";
    }
    if (step === 4 && !formData.password)
      newErrors.password = "Password is required.";
    if (step === 5 && formData.password !== formData.repeatPassword)
      newErrors.repeat = "Passwords do not match.";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep()) setStep((prev) => prev + 1);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  useEffect(() => {
    if (errors.repeat && formData.password === formData.repeatPassword) {
      setErrors((prev: any) => ({ ...prev, repeat: undefined }));
    }
  }, [formData.password, formData.repeatPassword, errors.repeat]);

  return (
    <div
      className="w-full h-screen flex items-center justify-center md:justify-start relative overflow-hidden"
      style={{
        backgroundImage: `url(${loginbackground})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      <style>{`::-webkit-scrollbar { display: none; }`}</style>

      <div
        className="relative h-[96vh] w-[90%] m-4 sm:w-[70%] md:w-[55%] lg:w-[45%] xl:w-[40%] rounded-[30px] text-[#F9F9F9] z-10 overflow-hidden"
        style={{
          backgroundImage: `url(${menubackground})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <motion.div
          className="h-full overflow-y-auto flex flex-col  items-center justify-center scrollbar-hide"
          variants={containerVariants}
          initial="hidden"
          animate="show"
        >
          <div className="w-[90%] sm:w-[85%] md:w-[87%] h-full mt-6">
            <div className="flex sm:items-center sm:flex-row flex-col-reverse sm:justify-between">
              <motion.div
                initial={{ opacity: 0, x: -25 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, ease: "easeOut", delay: 0.6 }}
              >
                <h2 className="text-[28px] font-bold mb-0">Sign Up</h2>
                <motion.p
                  initial={{ opacity: 0, y: 40 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.8, duration: 0.8, ease: "easeOut" }}
                  className="text-[#F9F9F9] text-[16px] mb-0"
                >
                  Create an account to join the community
                </motion.p>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, x: 40 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 1.0, duration: 0.1, ease: [0.25, 0.1, 0.25, 1] }}
              >
                <img src={Rectangle} alt="" className="h-[70px]" />
              </motion.div>
            </div>

            <div className="lg:px-8 md:px-6 sm:px-4 px-2">
              <motion.form
                variants={containerVariants}
                initial="hidden"
                animate="show"
                className="space-y-2 mt-4 text-left"
              >
                {step >= 1 && (
                  <motion.div variants={itemVariants}>
                    <div className="flex flex-wrap md:flex-nowrap gap-3">
                      <div className="w-full">
                        <label className="block mb-1 text-[16px]">First name</label>
                        <input
                          name="first"
                          value={formData.first}
                          onChange={handleChange}
                          className="w-full bg-transparent border border-[#f9f9f9] rounded-lg px-3 py-2 focus:outline-none focus:border-[#fb9c18]"
                        />
                      </div>
                      <div className="w-full">
                        <label className="block mb-1 text-[16px]">Last name</label>
                        <input
                          name="last"
                          value={formData.last}
                          onChange={handleChange}
                          className="w-full bg-transparent border border-[#f9f9f9] rounded-lg px-3 py-2 focus:outline-none focus:border-[#fb9c18]"
                        />
                      </div>
                    </div>
                    {errors.name && <motion.p variants={itemVariants} className="text-red-500 text-sm mt-1">{errors.name}</motion.p>}
                  </motion.div>
                )}

                {step >= 2 && (
                  <motion.div variants={itemVariants}>
                    <label className="block mb-1 text-[16px]">Username</label>
                    <input
                      name="username"
                      value={formData.username}
                      onChange={handleChange}
                      className="w-full bg-transparent border border-[#f9f9f9] rounded-lg px-3 py-2 focus:outline-none focus:border-[#fb9c18]"
                    />
                    {errors.username && <motion.p variants={itemVariants} className="text-red-500 text-sm mt-1">{errors.username}</motion.p>}
                  </motion.div>
                )}

                {step >= 3 && (
                  <motion.div variants={itemVariants}>
                    <label className="block mb-1 text-[16px]">Email</label>
                    <input
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      className={`w-full bg-transparent border ${errors.email ? "border-red-500" : "border-[#f9f9f9] focus:border-[#fb9c18]"} rounded-lg px-3 py-2 focus:outline-none`}
                    />
                    {errors.email && <motion.p variants={itemVariants} className="text-red-500 text-sm mt-1">{errors.email}</motion.p>}
                  </motion.div>
                )}

                {step >= 4 && (
                  <motion.div variants={itemVariants}>
                    <label className="block mb-1 text-[16px]">Password</label>
                    <div className="relative">
                      <input
                        name="password"
                        type={showPassword ? "text" : "password"}
                        value={formData.password}
                        onChange={handleChange}
                        className={`w-full bg-transparent border rounded-lg px-3 py-2 pr-10 focus:outline-none ${errors.password ? "border-red-500" : "border-[#f9f9f9] focus:border-[#fb9c18]"}`}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute top-3 right-3 text-[#f9f9f9] hover:text-[#fb9c18] transition-all duration-300"
                      >
                        {showPassword ? <FaEye className="text-[18px] hover:scale-110 transition-transform" /> : <FaEyeSlash className="text-[18px] hover:scale-110 transition-transform" />}
                      </button>
                    </div>
                    {errors.password && <motion.p variants={itemVariants} className="text-red-500 text-sm mt-1">{errors.password}</motion.p>}
                  </motion.div>
                )}

                {step >= 5 && (
                  <motion.div variants={itemVariants}>
                    <label className="block mb-1 text-[16px]">Repeat Password</label>
                    <div className="relative">
                      <input
                        name="repeatPassword"
                        type={showRepeatPassword ? "text" : "password"}
                        value={formData.repeatPassword}
                        onChange={handleChange}
                        className={`w-full bg-transparent border rounded-lg px-3 py-2 pr-10 focus:outline-none ${errors.repeat ? "border-red-500" : "border-[#f9f9f9] focus:border-[#fb9c18]"}`}
                      />
                      <button
                        type="button"
                        onClick={() => setShowRepeatPassword(!showRepeatPassword)}
                        className="absolute top-3 right-3 text-[#f9f9f9] hover:text-[#fb9c18] transition-all duration-300"
                      >
                        {showRepeatPassword ? <FaEye className="text-[18px] hover:scale-110 transition-transform" /> : <FaEyeSlash className="text-[18px] hover:scale-110 transition-transform" />}
                      </button>
                    </div>
                    {errors.repeat && <motion.p variants={itemVariants} className="text-red-500 text-sm mt-1">{errors.repeat}</motion.p>}
                  </motion.div>
                )}

                <motion.div variants={itemVariants}>
                  <div className="flex items-center ms-[-10px]">
                    <CustomCheckbox checked={checked} onChange={(e) => setChecked(e.target.checked)} />
                    <label className="text-[#f9f9f9] cursor-pointer text-[15px]">
                      Accept the <span className="text-[#fb9c18] underline">terms and conditions</span>
                    </label>
                  </div>
                </motion.div>

                <motion.div variants={itemVariants} className="flex items-center justify-center pt-2">
                  <motion.button
                    whileTap={{ scale: 0.97 }}
                    whileHover={{ scale: 1.03 }}
                    type="button"
                    onClick={() => { if (step < 5) handleNext(); }}
                    className="w-full sm:w-[70%] bg-[#fb9c18] text-white font-semibold py-3 rounded-lg transition hover:bg-[#e38a15]"
                  >
                    {step < 5 ? "Next" : "Sign up"}
                  </motion.button>
                </motion.div>
              </motion.form>
            </div>

            <motion.p variants={itemVariants} className="mt-2 text-center pb-4">
              Already have an account? <a href="/" className="text-[#fb9c18] underline font-semibold">Log in here!</a>
            </motion.p>
          </div>
        </motion.div>
      </div>
    </div>
  );
}

export default SignupPage;
